/**
 * `/documents/:id` — per-file workspace.
 *
 * Layout: header (filename + cards count + expiry) → action row (Check /
 * Summarize / Export PDF / Share link) → card list with verdicts in a
 * comments-rail layout.
 *
 * No model selector — server picks. Concurrency slider is bounded server-
 * side; we just expose the range.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DEFAULT_USER_PREFERENCES,
  type Card,
  type CheckSummary,
  type FileMeta,
  type VerdictCode,
} from '@eloquor/api-contract';
import { AppShell } from '../components/layout/AppShell';
import { ExpiryCountdown } from '../components/countdown/ExpiryCountdown';
import { CardList } from '../components/cards/CardRow';
import { SummaryStrip } from '../components/cards/SummaryStrip';
import { getDownloadUrl, getFile, listCards } from '../api/files';
import { streamCheck, streamSummarize } from '../api/check';
import { createShareAnalysis } from '../api/analyses';
import { ApiCallError } from '../api/client';
import { printPdfViaIframe } from '../report/rendering';
import { useSession } from '../hooks/useSession';

const EMPTY_SUMMARY: CheckSummary = {
  total: 0,
  ok: 0,
  ethicsvio: 0,
  miscut: 0,
  powertagged: 0,
  unclear: 0,
  error: 0,
  unchecked: 0,
};

export function DocumentPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  const highlightColor = user?.preferences.highlightColor ?? DEFAULT_USER_PREFERENCES.highlightColor;
  const [file, setFile] = useState<FileMeta | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [summary, setSummary] = useState<CheckSummary>(EMPTY_SUMMARY);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<'check' | 'summarize' | null>(null);
  const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const detail = await getFile(id);
      setFile(detail.file);
      const list = await listCards(id, {});
      setCards(list.cards);
      setSummary(list.summary);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not load.');
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Abort any in-flight SSE stream when the component unmounts (navigation,
  // route change, etc.). The server-side check keeps running and reconciles
  // credits on its own — we just stop consuming events to avoid writing to
  // unmounted state and to free the socket.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function runCheck() {
    if (!id || running) return;
    setError(null);
    setRunning('check');
    setProgress({ processed: 0, total: cards.length });
    abortRef.current = new AbortController();
    try {
      for await (const event of streamCheck(id, {}, abortRef.current.signal)) {
        if (event.type === 'card') {
          setCards((prev) =>
            prev.map((c) =>
              c.id === event.cardId
                ? {
                    ...c,
                    verdict: event.verdict as VerdictCode,
                    reason: event.reason,
                    errorId: event.errorId,
                  }
                : c
            )
          );
        } else if (event.type === 'progress') {
          setProgress({ processed: event.processed, total: event.total });
          setSummary(event.summary);
        } else if (event.type === 'done') {
          setSummary(event.summary);
        } else if (event.type === 'error') {
          setError(event.message);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check failed.');
    } finally {
      setRunning(null);
      setProgress(null);
    }
  }

  async function runSummarize() {
    if (!id || running) return;
    setError(null);
    setRunning('summarize');
    abortRef.current = new AbortController();
    try {
      for await (const event of streamSummarize(id, {}, abortRef.current.signal)) {
        if (event.type === 'progress') {
          setProgress({ processed: event.processed, total: event.total });
        } else if (event.type === 'group') {
          setCards((prev) =>
            prev.map((c) =>
              c.path.pocket === event.group.path.pocket &&
              c.path.hat === event.group.path.hat &&
              c.path.block === event.group.path.block
                ? { ...c, summary: event.group.summary, chains: event.group.chains }
                : c
            )
          );
        } else if (event.type === 'error') {
          setError(event.message);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Summarize failed.');
    } finally {
      setRunning(null);
      setProgress(null);
    }
  }

  async function makeShareLink() {
    if (!id) return;
    try {
      const res = await createShareAnalysis({ fileId: id });
      setShareUrl(`${window.location.origin}${res.path}`);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not create share link.');
    }
  }

  async function downloadOriginal() {
    if (!id) return;
    try {
      const { url } = await getDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not start download.');
    }
  }

  function exportPdf() {
    printPdfViaIframe({ cards, summary });
  }

  return (
    <AppShell>
      {!file ? (
        <div style={{ color: 'var(--fg-3)' }}>Loading…</div>
      ) : (
        <>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
            <div>
              <div className="auth-eyebrow">
                <span className="num">02</span> · Document
              </div>
              <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 4px' }}>
                {file.originalName}
              </h1>
              <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
                {file.cardCount} cards · uploaded {new Date(file.uploadedAt).toLocaleString()}
              </div>
            </div>
            <ExpiryCountdown expiresAt={file.expiresAt} />
          </header>

          <SummaryStrip summary={summary} />

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
            <button className="btn-primary" style={{ width: 'auto' }} onClick={runCheck} disabled={running !== null}>
              {running === 'check' ? 'Checking…' : 'Run check'}
            </button>
            <button className="btn-ghost" type="button" onClick={runSummarize} disabled={running !== null}>
              {running === 'summarize' ? 'Summarizing…' : 'Run summary'}
            </button>
            <button className="btn-ghost" type="button" onClick={exportPdf} disabled={cards.length === 0}>
              Export PDF
            </button>
            <button className="btn-ghost" type="button" onClick={makeShareLink} disabled={cards.length === 0}>
              Create share link
            </button>
            <button className="btn-ghost" type="button" onClick={downloadOriginal} disabled={!file}>
              Download original
            </button>
            {progress && (
              <span style={{ marginLeft: 12, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)' }}>
                {progress.processed} / {progress.total}
              </span>
            )}
          </div>

          {shareUrl && (
            <div className="banner" style={{ marginTop: 16 }}>
              Share link: <a href={shareUrl}>{shareUrl}</a>
            </div>
          )}

          {error && (
            <div className="banner danger" style={{ marginTop: 16 }}>
              {error}
            </div>
          )}

          <CardList cards={cards} highlightColor={highlightColor} />
        </>
      )}
    </AppShell>
  );
}
